// ---------------------------------------------------------------------------
// EmbeddingClient — Text embedding via Voyage AI or OpenAI REST APIs
// ---------------------------------------------------------------------------

export type EmbeddingProvider = 'voyage' | 'openai';

/** Embedding model IDs per provider */
const MODELS: Record<EmbeddingProvider, string> = {
  voyage: 'voyage-3',
  openai: 'text-embedding-3-small',
};

/** API endpoints per provider */
const ENDPOINTS: Record<EmbeddingProvider, string> = {
  voyage: 'https://api.voyageai.com/v1/embeddings',
  openai: 'https://api.openai.com/v1/embeddings',
};

/** Dimension counts per model (for pre-allocating / validation) */
export const EMBEDDING_DIMENSIONS: Record<EmbeddingProvider, number> = {
  voyage: 1024,
  openai: 1536,
};

export class EmbeddingClient {
  private apiKey: string | null = null;
  private provider: EmbeddingProvider = 'voyage';

  /** Configure the embedding client with a key and provider */
  configure(apiKey: string, provider: EmbeddingProvider = 'voyage'): void {
    this.apiKey = apiKey;
    this.provider = provider;
  }

  get isConfigured(): boolean {
    return this.apiKey !== null;
  }

  get dimensions(): number {
    return EMBEDDING_DIMENSIONS[this.provider];
  }

  // ════════════════════════════════════════════════════════════════════════
  // Embed one or more texts
  // ════════════════════════════════════════════════════════════════════════

  /**
   * Embed an array of texts. Returns one embedding vector per input text.
   * Batches automatically — both Voyage and OpenAI accept arrays.
   */
  async embed(texts: string[]): Promise<number[][]> {
    this._assertConfigured();
    if (texts.length === 0) return [];

    const endpoint = ENDPOINTS[this.provider];
    const model = MODELS[this.provider];

    const body: Record<string, unknown> =
      this.provider === 'voyage'
        ? { input: texts, model }
        : { input: texts, model, encoding_format: 'float' };

    const response = await this._fetchWithRetry(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Embedding API error (${response.status}): ${errorText}`);
    }

    const json = (await response.json()) as {
      data: Array<{ embedding: number[]; index: number }>;
    };

    // Sort by index to match input order
    return json.data
      .sort((a, b) => a.index - b.index)
      .map((d) => d.embedding);
  }

  /**
   * Embed a single text. Convenience wrapper.
   */
  async embedOne(text: string): Promise<number[]> {
    const results = await this.embed([text]);
    return results[0];
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal
  // ════════════════════════════════════════════════════════════════════════

  private _assertConfigured(): void {
    if (!this.apiKey) {
      throw new Error('EmbeddingClient not configured. Call configure(apiKey, provider) first.');
    }
  }

  private async _fetchWithRetry(
    url: string,
    init: RequestInit,
    retries = 3,
  ): Promise<Response> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await fetch(url, init);

        // Retry on rate limit with backoff
        if (response.status === 429) {
          const retryAfter = response.headers.get('retry-after');
          const waitMs = retryAfter
            ? parseInt(retryAfter, 10) * 1000
            : Math.pow(2, attempt) * 1000;
          await this._sleep(waitMs);
          continue;
        }

        // Retry on server errors
        if (response.status >= 500 && attempt < retries - 1) {
          await this._sleep(Math.pow(2, attempt) * 1000);
          continue;
        }

        return response;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt < retries - 1) {
          await this._sleep(Math.pow(2, attempt) * 1000);
        }
      }
    }

    throw lastError ?? new Error('Embedding request failed after retries');
  }

  private _sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
